<template>
    <div class="accept-terms-form">
        <div v-if="error">
            <div class="alert alert-danger" role="alert">
                {{ error }}
            </div>
        </div>
        <form @submit.prevent="acceptTerms()">
            <h1 class="accept-terms__title">
                {{ $t('Accept terms and privacy') }}
            </h1>
            <p class="accept-terms__text">
                {{ $t('To continue using your account, please review and accept our terms and privacy notice.') }}
            </p>
            <div class="d-flex justify-content-center mb-3">
                <ui-checkbox
                    id="accept_terms_and_privacy"
                    v-model="formData.accepted"
                    name="accept_terms_and_privacy"
                    class="accept-terms-form__checkbox"
                    :checked-value="true"
                    :unchecked-value="false"
                    :field="v$.formData.accepted"
                >
                    <span v-sanitize="$t('I understand and accept the <a href=\'{terms_url}\' target=\'_blank\' rel=\'noopener\'>terms and conditions</a> and the <a href=\'{privacy_url}\' target=\'_blank\' rel=\'noopener\'>privacy policy</a>.', { terms_url: getCheckboxLink('terms-and-conditions'), privacy_url: getCheckboxLink('privacy-policy') })"></span>
                </ui-checkbox>
            </div>
            <ui-button
                :loading="loading"
                class="w-100"
                type="submit"
                variant="primary"
            >
                {{ $t('Continue') }}
            </ui-button>
        </form>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { helpers, sameAs } from '@vuelidate/validators';

import UiButton from '@/components/ui/buttons/Button.vue';
import UiCheckbox from '@/components/ui/inputs/Checkbox.vue';

export default {
    name: 'AcceptTerms',
    components: {
        UiButton,
        UiCheckbox,
    },
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    data() {
        return {
            loading: false,
            error: null,
            formData: {
                accepted: false,
            },
        };
    },
    computed: {
        ...mapGetters('ui', ['sites']),
    },
    validations() {
        return {
            formData: {
                accepted: {
                    sameAs: helpers.withMessage(this.$t('It is necessary to accept the terms and privacy policy'), sameAs(true)),
                    $autoDirty: true,
                },
            },
        };
    },
    methods: {
        getCheckboxLink(type) {
            const { site_id } = this.$route.query;
            const site = this.sites?.find(site => site.site_id === site_id);

            if (site) {
                const site_name = site.name.toLowerCase().replace(' ', '-');

                if (this.$te(`${site_name}.${type}`)) {
                    return this.$t(`${site_name}.${type}`);
                }
            }

            return this.$t(`accounts.${type}`);
        },
        async acceptTerms() {
            try {
                this.v$.$touch();

                if (this.v$.$invalid) {
                    return;
                }

                this.loading = true;

                const { data } = await this.axiosAccount.post('/api/accounts/accept-terms', {
                    accept_terms_conditions: true,
                    accept_privacy_notes: true,
                });

                const next_url = data?.redirect_url;
                if (next_url) {
                    window.location.replace(next_url);
                } else {
                    this.$router.push('/');
                }
            } catch (err) {
                this.error = err?.response?.data?.message || this.$t('An unexpected error occurred. Please try again.');
            } finally {
                setTimeout(() => {
                    this.loading = false;
                }, 500);
            }
        },
    },
};
</script>

<style lang="scss">
.accept-terms-form {
    &__checkbox {
        a {
            color: var(--color-primary-800);
            font-weight: 600;
            text-transform: lowercase;
        }
    }
}
</style>
